'use client';

import {useCallback, useMemo} from 'react';
import useSWRMutation from 'swr/mutation';
import {buyAssetOrSignIn, getSandboxId} from '@/lib/utils/purchase.utils';
import {useLocale} from '@epic-mw/i18n';
import {useAccountVerifyLogin} from '../auth';
import {useSearchParams} from 'next/navigation';
import {isSameOrigin} from '@/lib/utils/page.utils';
import {isValidSalesChannel} from '@/services/utils/purchase.utils';
import {FN_MERCHANT_GROUP, NAV_OWNER} from '@/constants';
import {PurchaseOfferType} from '@/types';
import {PurchaseContextProps, usePurchaseContext} from '@/components';

interface UsePurchaseProps {
	offerId: string;
	sandboxId: string;
	offerType?: PurchaseOfferType;
	merchantGroup?: string;
	salesChannel?: string;
}

export const usePurchaseStartCallback = (offerId: string, sandboxId: string) => {
	const {actions, state: purchaseState} = usePurchaseContext();
	const isLoading = !['inactive', 'download'].includes(purchaseState);

	const onClick = useMemo(() => {
		if (!offerId || !sandboxId || purchaseState !== 'inactive') {
			return () => {};
		}

		return () => {
			return (actions as PurchaseContextProps<'inactive'>['actions']).inactive.start({
				offerId,
				sandboxId
			});
		};
	}, [offerId, sandboxId, purchaseState, actions]);

	return {
		onClick,
		isLoading
	};
};

export const usePurchase = ({
	offerId,
	sandboxId: namespace,
	offerType,
	merchantGroup,
	salesChannel
}: UsePurchaseProps) => {
	const locale = useLocale();
	const {verifyAuthenticated} = useAccountVerifyLogin(locale);
	const {actions, state} = usePurchaseContext<'checkout'>();

	const handler = useCallback(() => {
		if (
			verifyAuthenticated({
				redirectUrlParams: {purchaseIntentId: getSandboxId({offerId, namespace})}
			})
		) {
			if (!offerId || !namespace) {
				console.warn('No product selected...', offerId, namespace);
				return undefined;
			}

			return buyAssetOrSignIn({
				locale,
				canPurchase: true,
				namespace,
				id: offerId,
				offerType,
				merchantGroup,
				salesChannel
			});
		}
	}, [verifyAuthenticated, locale, offerId, namespace, offerType, salesChannel, merchantGroup]);

	const {trigger: buyOrSignIn, ...query} = useSWRMutation('buyorsignin', handler, {
		onError: (error: Error, _key, _config) => {
			console.debug('onError: ', error);
			// purchaseFailure(namespace, offerId);
			actions[state]?.cancel();
		},
		onSuccess: (result, _key, _config) => {
			console.debug('onSuccess', result);
			// refreshAccountTotalBalance();
		}
	});

	return useMemo(() => ({buyOrSignIn, ...query}), [buyOrSignIn, query]);
};

export const usePurchaseParams = () => {
	const searchParams = useSearchParams();
	// merchantGroup only allows 'FN_MKT'.
	const merchantGroup = searchParams.get('merchantGroup') || '';
	// salesChannel only allows alphabet numeric, dash and underline, with a limit of max 128 characters.
	const salesChannel = searchParams.get('salesChannel') || '';
	// redirectUrl only allows epic domains
	const redirectUrl = searchParams.get('redirectUrl') || undefined;

	const params = useMemo(() => {
		const buildReturnUrl = (redirectUrl: string) => {
			const returnTo = new URL(redirectUrl);

			// 1 = redeemed, 0 = failed
			returnTo.searchParams.set('r', '1');
			returnTo.searchParams.set('source', NAV_OWNER);

			return returnTo.toString();
		};

		return {
			merchantGroup: merchantGroup === FN_MERCHANT_GROUP ? FN_MERCHANT_GROUP : undefined,
			salesChannel: isValidSalesChannel(salesChannel) ? salesChannel : undefined,
			redirectUrl: isSameOrigin(redirectUrl) ? buildReturnUrl(redirectUrl!) : undefined
		};
	}, [merchantGroup, salesChannel, redirectUrl]);

	return params;
};

// const formatOffers = (offers?: (Offer | OfferLowercase)[]) =>
// 	offers?.map((offer) => ({
// 		offerId: (offer as Offer)?.OfferId || (offer as OfferLowercase)?.offerid,
// 		sandboxId: (offer as Offer)?.OfferNamespace || (offer as OfferLowercase)?.offernamespace,
// 	}));
// export const useCheckout = (lineOffers: LineOffer[]) => {
// 	const locale = useLocale();
// 	const { verifyAuthenticated } = useAccountVerifyLogin(locale);

// 	const handler = useCallback(() => {
// 		if (!verifyAuthenticated()) {
// 			return noopAsync(null);
// 		}
// 		return onStartCheckout(lineOffers, locale);
// 	}, [lineOffers, locale]);

// 	const onSettled = (value: Error | Receipt | ReceiptLower) => {
// 		// need check wallet balance both on success and cancel/error as user can add wallet balance but didn't buy anything
// 	};

// 	const { trigger: startCheckout, ...query } = useSWRMutation<Receipt | ReceiptLower, Error>("startCheckout", handler, {
// 		onError: (error, key, config) => {
// 			// contextDispatch({
// 			// 	type: CartActions.SET_CHECKOUT_ERROR,
// 			// 	payload: { error },
// 			// });

// 			console.log("onError: ", error);
// 			onSettled(error);
// 		},
// 		onSuccess: (result, key, config) => {
// 			const offers: PurchaseReceiptOffers[] | PurchaseReceiptOffersLowercase[] = (result as Receipt)?.Offers || (result as ReceiptLower)?.offers;
// 			const formattedOffers = formatOffers(offers);
// 			const earnedEpicRewards =
// 				(result as Receipt)?.formattedEarnedEpicRewards || (result as ReceiptLower)?.formattedearnedepicrewards;

// 			// send toast event
// 			// formattedOffers?.forEach((offer) => {
// 			// purchaseSuccess(offer.sandboxId, offer.offerId, result);
// 			// });

// 			console.log("onSuccess: ", result, { formattedOffers, earnedEpicRewards });
// 			onSettled(result);

// 			// history.push({
// 			// 	pathname: generateRoutePath("/cart/success"),
// 			// 	state: {
// 			// 		// launcher receipt properties are all lowercase
// 			// 		transactionId: (result as Receipt)?.TransactionId || (result as ReceiptLower)?.transactionid,
// 			// 		offers: formattedOffers,
// 			// 		earnedEpicRewards,
// 			// 	},
// 			// });
// 		},
// 	});

// 	return useMemo(() => ({ startCheckout, ...query }), [startCheckout, query]);
// };

// const reducer = (data: GetCartItemsQuery) =>
//     data?.Cart?.cartItems?.elements.reduce<CartItem[]>((acc, item) => {
//         // Using reduce and being explicit about the object here so that the current version of ts can properly infer
//         if (item && item.catalogNs.parent && item.catalogNs.store) {
//             acc.push(item as CartItem);
//         }

//         return acc;
//     }, []) || undefined;

// export const useGetCartItems = (config?: UseQueryOptions<any, unknown, any>) => {
//     const accountId = useSelector(selectAccountId);
//     const enabled = Boolean(accountId) && (config?.enabled ?? true);

//     const {data: cartItems, ...query} = useGraphqlData<
//         GetCartItemsQuery,
//         GetCartItemsQueryVariables,
//         CartItem[] | undefined
//     >({
//         query: GetCartItemsDocument,
//         config: {
//             ...config,
//             enabled,
//         },
//         reducer,
//     });

//     return {cartItems, query};
// };
